import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

export const useVehicules = () => {
  const { apiCall, API_BASE_URL } = useApi();
  const [vehicules, setVehicules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVehicules = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.category && filters.category !== 'Tous') {
        queryParams.append('slug', filters.category.toLowerCase());
      }
      
      const queryString = queryParams.toString();
      const endpoint = queryString ? `/vehicules?${queryString}` : '/vehicules';
      
      const data = await apiCall(endpoint);
      console.log("Données véhicules récupérées:", data?.length || 0, "véhicules");
      setVehicules(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des véhicules:', error);
      setError(error.message);
      setVehicules([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  return { vehicules, loading, error, fetchVehicules };
};

export const useVehiculeActions = () => {
  const { apiCall, API_BASE_URL } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getVehicule = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 getVehicule appelé avec id:', id);
      const data = await apiCall(`/vehicules/${id}`);
      console.log('✅ getVehicule - Réponse API:', data);
      return data;
    } catch (error) {
      console.error('❌ getVehicule - Erreur:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const createVehicule = useCallback(async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer le tenant_id depuis le localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = userData.tenant_id;
      
      const response = await apiCall(`${API_BASE_URL}/vehicules`, {
        method: 'POST',
        headers: {
          // Content-Type sera automatiquement supprimé pour FormData
          'x-tenant-id': tenantId
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, API_BASE_URL]);

  const updateVehicule = useCallback(async (id, formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiCall(`${API_BASE_URL}/vehicules/${id}`, {
        method: 'PUT',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du véhicule:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, API_BASE_URL]);

  const deleteVehicule = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiCall(`${API_BASE_URL}/vehicules/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la suppression du véhicule:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [apiCall, API_BASE_URL]);

  return {
    getVehicule,
    createVehicule,
    updateVehicule,
    deleteVehicule,
    loading,
    error
  };
};

// Hook pour récupérer les types/catégories de véhicules dynamiquement
export const useVehicleTypes = () => {
  const { apiCall } = useApi();
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [categories, setCategories] = useState(['Tous']);
  const [categoryMapping, setCategoryMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVehicleTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiCall('/vehicules/types');
      console.log('✅ Types de véhicules récupérés:', data);

      const types = Array.isArray(data) ? data : (data.types || []);
      setVehicleTypes(types);

      // Créer les catégories uniques à partir des types
      const uniqueCategories = ['Tous'];
      const mapping = {};

      types.forEach(type => {
        const category = type.category || type.slug || type.name;
        if (category && !uniqueCategories.includes(category)) {
          uniqueCategories.push(category);
        }
        // Créer le mapping catégorie -> ID
        if (type.id) {
          mapping[category] = type.id;
        }
      });

      setCategories(uniqueCategories);
      setCategoryMapping(mapping);

    } catch (err) {
      console.error('❌ Erreur récupération types de véhicules:', err);
      setError(err.message);
      // Fallback sur les valeurs par défaut
      setCategories(['Tous', 'Engin', 'Camion', 'Camionnette', 'Voiture', 'Moto']);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchVehicleTypes();
  }, [fetchVehicleTypes]);

  return {
    vehicleTypes,
    categories,
    categoryMapping,
    loading,
    error,
    refetch: fetchVehicleTypes
  };
};